import React, { useEffect } from "react";
import { ArrowUpRight, Plus, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CustomHeader } from "../components/AuthHeader";
import { useAuthStore } from "../features/auth/authStore";
import { useOrderStore } from "../features/orders/orderStore";

export default function DashboardScreen() {
  const { user } = useAuthStore();
  const { orders, fetchCustomerOrdersByStatuses } = useOrderStore();
  const navigate = useNavigate();

  const welcomeTitle = `Welcome, ${user?.first_name ?? "there"} 👋`;

  useEffect(() => {
  fetchCustomerOrdersByStatuses([
    "open",
    "collected",
    "received",
    "processing",
    "ready_to_pickup",
    "ready_for_delivery",
    "delivered",
  ]);
}, [fetchCustomerOrdersByStatuses]);

  // Counts
  const ongoingCount = orders.filter((o) =>
    ["open", "collected", "received", "ready_for_delivery", "ready_to_pickup"].includes(o.status)
  ).length;

  // Orders waiting on the customer: review the item list, or pay
  const needsAction = orders.filter(
    (o) =>
      o.status !== "delivered" &&
      (o.approval_status === "pending" ||
        (o.approval_status === "approved" && o.amount_due > 0)),
  );

  const orderProcessCount = orders.filter((o) => o.status === "processing").length;
  const deliveredCount = orders.filter((o) => o.status === "delivered").length;

  const statusRows = [
    {
      label: "Ongoing Orders",
      count: ongoingCount,
      icon: <Clock className="h-4 w-4 text-[#44B6CE]" />,
      iconBg: "bg-[#44B6CE]/10",
      countColor: "text-[#44B6CE]",
      onPress: () => navigate("/order/track"),
    },
    {
      label: "Orders In Process",
      count: orderProcessCount,
      icon: <Clock className="h-4 w-4 text-yellow-500" />,
      iconBg: "bg-yellow-50",
      countColor: "text-yellow-500",
      onPress: () => navigate("/order/track"),
    },
    {
      label: "Delivered",
      count: deliveredCount,
      icon: <CheckCircle className="h-4 w-4 text-green-500" />,
      iconBg: "bg-green-50",
      countColor: "text-green-500",
      onPress: () => navigate("/order/history"),
    },
  ];

  return (
    <div className="bg-gray-50 pb-8">

      {/* Header */}
      <div className="bg-white px-6 pt-5 pb-5 shadow-sm rounded-b-3xl md:rounded-3xl">
        <CustomHeader
          title={welcomeTitle}
          subtitle="Manage your laundry orders quickly and easily."
        />
      </div>

      <div className="px-6 pt-6 space-y-6 md:px-0 md:grid md:grid-cols-2 md:gap-6 md:space-y-0 md:items-start">
       <div className="space-y-6">
        {/* Place New Order CTA */}
        <button
          onClick={() => navigate("/order/new")}
          className="w-full flex items-center justify-between bg-[#44B6CE] rounded-2xl px-5 py-4 shadow-md active:opacity-90 transition"
        >
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-xl p-2">
              <Plus className="h-5 w-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-white font-bold text-base">Place New Order</p>
              <p className="text-white/70 text-xs">Fast pickup & delivery</p>
            </div>
          </div>
          <ArrowUpRight className="h-5 w-5 text-white/80" />
        </button>

        {needsAction.length > 0 && (
          <div className="space-y-2">
            {needsAction.map((o) => (
              <button
                key={o.id}
                onClick={() => navigate(`/order/details?order=${o.id}`)}
                className="w-full flex items-center justify-between rounded-2xl bg-orange-50 border border-orange-200 px-4 py-3.5 active:bg-orange-100 transition"
              >
                <div className="flex items-center gap-3 text-left">
                  <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      {o.approval_status === "pending" ? "Review your items" : "Payment due"}
                    </p>
                    <p className="text-xs text-gray-500">Order #{o.order_no}</p>
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-orange-400" />
              </button>
            ))}
          </div>
        )}

       </div>

        {/* Overview */}
        <div className="md:bg-white md:rounded-2xl md:border md:border-gray-100 md:p-5 md:shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Overview
          </p>

          {/* Detail rows */}
          <div className="space-y-2">
            {statusRows.map((row, idx) => (
              <button
                key={idx}
                onClick={row.onPress}
                className="w-full flex items-center justify-between rounded-2xl bg-white border border-gray-100 px-4 py-3.5 shadow-sm active:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <div className={`${row.iconBg} rounded-xl p-2`}>
                    {row.icon}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">{row.label}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${row.countColor}`}>
                    {row.count}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-gray-300" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}